import type { FC, FormEvent, ReactNode } from 'react'
import type { BillingCycle, Customer, PlanId } from '@/types'

import { useState } from 'react'
import { formatRupiah } from '@/lib/format'
import { TODAY, addDays, formatDateLong } from '@/lib/date'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { EMAIL_PATTERN, FormField, fieldMessageId } from '@/components/dashboard/form-field'
import { PLANS, computeMrr, getPlan } from '@/data/plans'
import { CITIES, getProvince } from '@/constants/dashboard'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface AddCustomerDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    nextId: string
    isEmailTaken: (email: string) => boolean
    onCreate: (customer: Customer) => void
}

type FormState = {
    company: string
    contact: string
    email: string
    plan: PlanId
    cycle: BillingCycle
    seats: string
    city: string
    trial: boolean
}

type FormErrors = Partial<Record<keyof FormState, string>>

const INITIAL_FORM: FormState = {
    company: '',
    contact: '',
    email: '',
    plan: 'growth',
    cycle: 'monthly',
    seats: '5',
    city: '',
    trial: true
}

const FIELD_ORDER: (keyof FormState)[] = ['company', 'contact', 'email', 'plan', 'seats', 'city']

const PLAN_ITEMS = PLANS.map((plan) => ({ value: plan.id, label: plan.name }))
const CITY_ITEMS = CITIES.map((item) => ({ value: item.city, label: item.city }))

const fieldId = (name: keyof FormState): string => `add-customer-${name}`

export const AddCustomerDialog: FC<AddCustomerDialogProps> = ({ open, onOpenChange, nextId, isEmailTaken, onCreate }): ReactNode => {
    const [form, setForm] = useState<FormState>(INITIAL_FORM)
    const [errors, setErrors] = useState<FormErrors>({})

    const seats = Number(form.seats)
    const validSeats = Number.isInteger(seats) && seats > 0
    const estimatedMrr = validSeats ? computeMrr(form.plan, form.cycle, seats) : null
    const plan = getPlan(form.plan)

    const update = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
        setForm((current) => ({ ...current, [key]: value }))
        if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }))
    }

    const validate = (): FormErrors => {
        const next: FormErrors = {}
        const company = form.company.trim()
        const email = form.email.trim().toLowerCase()

        if (!company) next.company = 'Nama perusahaan wajib diisi.'
        else if (company.length < 3) next.company = 'Nama perusahaan minimal 3 karakter.'

        if (!form.contact.trim()) next.contact = 'Nama kontak wajib diisi.'

        if (!email) next.email = 'Email wajib diisi.'
        else if (!EMAIL_PATTERN.test(email)) next.email = 'Format email belum benar, contoh: nama@perusahaan.id'
        else if (isEmailTaken(email)) next.email = 'Email ini sudah terdaftar sebagai pelanggan.'

        if (!validSeats) next.seats = 'Jumlah pengguna minimal 1.'
        else if (form.plan === 'starter' && seats > 3) next.seats = 'Paket Starter maksimal 3 pengguna.'
        else if (seats > 1000) next.seats = 'Maksimal 1.000 pengguna per akun.'

        if (!form.city) next.city = 'Pilih kota tempat pelanggan beroperasi.'

        return next
    }

    const reset = (): void => {
        setForm(INITIAL_FORM)
        setErrors({})
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        const nextErrors = validate()
        setErrors(nextErrors)

        const firstInvalid = FIELD_ORDER.find((key) => nextErrors[key])

        if (firstInvalid) {
            document.getElementById(fieldId(firstInvalid))?.focus()
            return
        }

        onCreate({
            id: nextId,
            company: form.company.trim(),
            contact: form.contact.trim(),
            email: form.email.trim().toLowerCase(),
            plan: form.plan,
            status: form.trial ? 'trial' : 'active',
            cycle: form.cycle,
            mrr: computeMrr(form.plan, form.cycle, seats),
            seats,
            city: form.city,
            province: getProvince(form.city),
            joinedAt: TODAY,
            canceledAt: null,
            trialEndsAt: form.trial ? addDays(TODAY, 14) : null,
            health: 70,
            paymentMethod: 'va'
        })

        reset()
        onOpenChange(false)
    }

    const describedBy = (key: keyof FormState): string | undefined => (errors[key] ? fieldMessageId(fieldId(key)) : undefined)

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                onOpenChange(next)
                if (!next) reset()
            }}
        >
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg'>
                <form onSubmit={onSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>
                            Tambah pelanggan baru
                        </DialogTitle>
                        <DialogDescription>
                            Data akun dan paket awal. Nomor pelanggan {nextId} akan dibuat otomatis.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <FormField id={fieldId('company')} label='Nama perusahaan' required error={errors.company} className='sm:col-span-2'>
                            <Input
                                id={fieldId('company')}
                                value={form.company}
                                onChange={(event) => update('company', event.target.value)}
                                placeholder='Contoh: Kopi Senja Nusantara'
                                autoComplete='organization'
                                aria-invalid={Boolean(errors.company)}
                                aria-describedby={describedBy('company')}
                            />
                        </FormField>

                        <FormField id={fieldId('contact')} label='Nama kontak' required error={errors.contact}>
                            <Input
                                id={fieldId('contact')}
                                value={form.contact}
                                onChange={(event) => update('contact', event.target.value)}
                                placeholder='Nama penanggung jawab'
                                autoComplete='name'
                                aria-invalid={Boolean(errors.contact)}
                                aria-describedby={describedBy('contact')}
                            />
                        </FormField>

                        <FormField id={fieldId('email')} label='Email kontak' required error={errors.email}>
                            <Input
                                id={fieldId('email')}
                                type='email'
                                inputMode='email'
                                value={form.email}
                                onChange={(event) => update('email', event.target.value)}
                                placeholder='nama@perusahaan.id'
                                autoComplete='email'
                                aria-invalid={Boolean(errors.email)}
                                aria-describedby={describedBy('email')}
                            />
                        </FormField>

                        <FormField id={fieldId('plan')} label='Paket' required error={errors.plan}>
                            <Select
                                items={PLAN_ITEMS}
                                value={form.plan}
                                onValueChange={(value) => {
                                    if (value) update('plan', value as PlanId)
                                }}
                            >
                                <SelectTrigger id={fieldId('plan')} className='w-full'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PLANS.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>
                                            <span className='size-2 rounded-full' style={{ background: item.color }} aria-hidden='true' />
                                            {item.name}
                                            <span className='text-xs text-muted-foreground'>{formatRupiah(item.price)}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormField>

                        <FormField
                            id={fieldId('seats')}
                            label='Jumlah pengguna'
                            required
                            error={errors.seats}
                            hint={`Termasuk ${plan.includedSeats} pengguna${plan.seatPrice > 0 ? `, tambahan ${formatRupiah(plan.seatPrice)} per pengguna` : ''}`}
                        >
                            <Input
                                id={fieldId('seats')}
                                type='number'
                                inputMode='numeric'
                                min={1}
                                max={1000}
                                value={form.seats}
                                onChange={(event) => update('seats', event.target.value)}
                                aria-invalid={Boolean(errors.seats)}
                                aria-describedby={errors.seats ? describedBy('seats') : fieldMessageId(fieldId('seats'))}
                            />
                        </FormField>

                        <FormField id={fieldId('city')} label='Kota' required error={errors.city}>
                            <Select
                                items={CITY_ITEMS}
                                value={form.city || null}
                                onValueChange={(value) => {
                                    if (value) update('city', value as string)
                                }}
                            >
                                <SelectTrigger
                                    id={fieldId('city')}
                                    className='w-full'
                                    aria-invalid={Boolean(errors.city)}
                                    aria-describedby={describedBy('city')}
                                >
                                    <SelectValue placeholder='Pilih kota' />
                                </SelectTrigger>
                                <SelectContent>
                                    {CITY_ITEMS.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormField>

                        <div className='space-y-1.5'>
                            <span className='text-sm leading-none font-medium'>
                                Siklus tagihan
                            </span>
                            <SegmentedControl
                                label='Siklus tagihan'
                                value={form.cycle}
                                onChange={(value) => update('cycle', value)}
                                options={[
                                    { value: 'monthly', label: 'Bulanan' },
                                    { value: 'annual', label: 'Tahunan' }
                                ]}
                                className='flex w-full [&>button]:flex-1'
                            />
                        </div>
                    </div>

                    <div className='flex items-start gap-3 rounded-lg border bg-muted/40 p-3'>
                        <Checkbox
                            id={fieldId('trial')}
                            checked={form.trial}
                            onCheckedChange={(checked) => update('trial', checked === true)}
                            className='mt-0.5'
                        />
                        <div className='min-w-0'>
                            <Label htmlFor={fieldId('trial')}>
                                Mulai dengan trial 14 hari
                            </Label>
                            <p className='mt-1 text-xs text-muted-foreground'>
                                {form.trial
                                    ? `Penagihan pertama dimulai ${formatDateLong(addDays(TODAY, 14))}.`
                                    : 'Tagihan pertama diterbitkan hari ini.'}
                            </p>
                        </div>
                    </div>

                    <div className='flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm'>
                        <span className='text-muted-foreground'>
                            Estimasi MRR
                        </span>
                        <span className='font-semibold text-foreground tabular-nums'>
                            {estimatedMrr === null ? 'Lengkapi jumlah pengguna' : `${formatRupiah(estimatedMrr)} / bulan`}
                        </span>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Batal
                        </DialogClose>
                        <Button type='submit'>
                            Simpan pelanggan
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
